import { useState } from "react";
import { Accessibility, RotateCcw, Type, Contrast, Link2, BookOpen } from "lucide-react";

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState({});

  const toggle = (key, action) => {
    const newActive = { ...active, [key]: !active[key] };
    setActive(newActive);
    const wrapper = document.getElementById("main-wrapper");
    if (!wrapper) return;

    if (key === "increase-text") {
      document.documentElement.style.fontSize = newActive[key] ? "120%" : "";
    } else if (key === "decrease-text") {
      document.documentElement.style.fontSize = newActive[key] ? "85%" : "";
    } else if (key === "grayscale") {
      wrapper.style.filter = newActive[key] ? "grayscale(100%)" : "";
    } else if (key === "high-contrast") {
      wrapper.style.filter = newActive[key] ? "contrast(1.5)" : "";
    } else if (key === "negative-contrast") {
      wrapper.style.filter = newActive[key] ? "invert(1)" : "";
    } else if (key === "light-bg") {
      wrapper.style.backgroundColor = newActive[key] ? "#FFFFFF" : "";
    } else if (key === "underline-links") {
      const links = wrapper.querySelectorAll("a");
      links.forEach(l => l.style.textDecoration = newActive[key] ? "underline" : "");
    } else if (key === "readable-font") {
      wrapper.style.fontFamily = newActive[key] ? "Arial, Helvetica, sans-serif" : "";
      wrapper.style.letterSpacing = newActive[key] ? "0.05em" : "";
      wrapper.style.lineHeight = newActive[key] ? "1.8" : "";
    }
  };

  const resetAll = () => {
    setActive({});
    document.documentElement.style.fontSize = "";
    const wrapper = document.getElementById("main-wrapper");
    if (!wrapper) return;
    wrapper.style.filter = "";
    wrapper.style.backgroundColor = "";
    wrapper.style.fontFamily = "";
    wrapper.style.letterSpacing = "";
    wrapper.style.lineHeight = "";
    const links = wrapper.querySelectorAll("a");
    links.forEach(l => l.style.textDecoration = "");
  };

  const hasActive = Object.values(active).some(v => v);

  const tools = [
    { key: "increase-text", icon: "A+", label: "Increase Text" },
    { key: "decrease-text", icon: "A-", label: "Decrease Text" },
    { key: "grayscale", icon: "◐", label: "Grayscale" },
    { key: "high-contrast", icon: "◑", label: "High Contrast" },
    { key: "negative-contrast", icon: "◒", label: "Negative Contrast" },
    { key: "light-bg", icon: "☀", label: "Light Background" },
    { key: "underline-links", icon: "U", label: "Links Underline" },
    { key: "readable-font", icon: "Aa", label: "Readable Font" },
  ];

  return (
    <>
      {/* Wheelchair toggle button */}
      <button
        onClick={() => setOpen(!open)}
        className={`fixed left-4 top-20 z-[60] w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all ${
          hasActive ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground"
        } hover:scale-110`}
        aria-label="Open accessibility tools"
        data-testid="accessibility-toggle"
      >
        <Accessibility className="w-5 h-5" />
      </button>

      {/* Tools panel */}
      {open && (
        <div
          className="fixed left-4 top-32 z-[60] w-56 bg-card border border-border rounded-2xl shadow-xl p-4 space-y-2 animate-fade-in-up"
          role="dialog"
          aria-label="Accessibility Tools"
          data-testid="accessibility-panel"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold">Accessibility Tools</h3>
            <button onClick={() => setOpen(false)} className="text-xs text-muted-foreground hover:text-foreground" aria-label="Close accessibility tools">X</button>
          </div>

          {tools.map(tool => (
            <button
              key={tool.key}
              onClick={() => toggle(tool.key)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                active[tool.key] ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted/50 text-muted-foreground"
              }`}
              aria-pressed={active[tool.key] || false}
              aria-label={tool.label}
            >
              <span className="w-6 text-center font-mono text-xs">{tool.icon}</span>
              <span>{tool.label}</span>
            </button>
          ))}

          {hasActive && (
            <button
              onClick={resetAll}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm bg-destructive/10 text-destructive hover:bg-destructive/20 mt-2"
              aria-label="Reset all accessibility settings"
              data-testid="accessibility-reset"
            >
              <RotateCcw className="w-3 h-3" /> Reset All
            </button>
          )}
        </div>
      )}
    </>
  );
}
